import type { Category, CategoryCheck } from "../api/category";
import type { CheckTypeKey } from "../api/pricelist";
import type { UserLimits } from "../api/user";
import { CHECK_NAME_LABELS, labelForCheckName, resolveCheckNameKey } from "./checkLabels";

const normalizeTextKey = (value: string): string =>
  value.trim().replace(/\s+/g, " ").toLowerCase();

const checkLabel = (check: CategoryCheck): string | null => {
  const description = check.description?.trim();
  if (description) return description;
  const code = check.code?.trim();
  if (code) return labelForCheckName(code) ?? code;
  return check.id?.trim() ?? null;
};

const checkKeys = (check: CategoryCheck, label: string): string[] => {
  const keys = new Set<string>();
  const id = check.id?.trim();
  if (id) keys.add(`id:${id}`);
  const code = check.code?.trim();
  const codeKey = code ? resolveCheckNameKey(code) : undefined;
  if (codeKey) keys.add(`code:${codeKey}`);
  const descKey = check.description ? resolveCheckNameKey(check.description) : undefined;
  if (descKey) keys.add(`code:${descKey}`);
  for (const [key, text] of Object.entries(CHECK_NAME_LABELS)) {
    if (normalizeTextKey(text) === normalizeTextKey(label)) keys.add(`code:${key}`);
  }
  keys.add(`label:${normalizeTextKey(label)}`);
  return [...keys];
};

export const entitlementCategoryIds = (
  userLimits: UserLimits | null | undefined,
  checkType: CheckTypeKey
): string[] => {
  const ids = userLimits?.entitlements?.[checkType]?.categoryIds ?? [];
  return [...new Set(ids.map((id) => id?.trim()).filter((id): id is string => Boolean(id)))];
};

export const resolveTariffCheckLabels = (
  userLimits: UserLimits | null | undefined,
  checkType: CheckTypeKey,
  categories: Category[]
): string[] => {
  const ids = new Set(entitlementCategoryIds(userLimits, checkType));
  if (!ids.size) return [];

  const byId = new Map(
    categories.flatMap((item) => (item.id ? [[item.id, item] as const] : []))
  );
  const seen = new Set<string>();
  const labels: string[] = [];

  for (const id of ids) {
    const category = byId.get(id);
    if (!category) continue;
    for (const check of category.checks ?? []) {
      const label = checkLabel(check);
      if (!label) continue;
      const keys = checkKeys(check, label);
      if (keys.some((key) => seen.has(key))) continue;
      keys.forEach((key) => seen.add(key));
      labels.push(label);
    }
  }

  return labels;
};
